import { FC } from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { CaptionSM, EyebrowLG, HeadingSM, TitleLG, TitleSM, TitleXXS } from '@/atoms/Text'
import { ValidEarlyBirdDiscount } from '@/services/Theatrical/queries'
import { useTranslate } from '@/utils/translate/translate-client'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import { CountdownTimer } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/EarlyBirdBanner/CountdownTimer'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { useRestrictionCopy } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'

interface Props {
  backgroundImage: PromoImage
  foregroundImage: PromoImage
  accentColor: string
  openingWeek: string
  earlyBirdDiscount: ValidEarlyBirdDiscount
  code: string
  refetch?: () => Promise<void>
  className?: string
}

export const EarlyBirdBanner: FC<Props> = ({
  backgroundImage,
  foregroundImage,
  accentColor,
  openingWeek,
  earlyBirdDiscount,
  code,
  refetch,
  className,
}) => {
  const { t } = useTranslate('tickets')
  const restrictionCopy = useRestrictionCopy(earlyBirdDiscount, openingWeek)

  return (
    <div className={className}>
      <div className="relative z-[39] h-[140px] rounded-xl @container">
        <div className={'absolute h-full w-full overflow-hidden rounded-xl'}>
          {/* Background Image */}
          <Image src={backgroundImage.url} className="z-10 object-cover" fill alt={''} quality={50} />
          {/* Foreground Image */}
          <Image
            src={foregroundImage.url}
            className="absolute inset-y-0 right-0 z-10 hidden @2xl:block"
            height={140}
            width={(foregroundImage.width / foregroundImage.height) * 140}
            alt={''}
          />
        </div>

        <div className="absolute left-0 top-0 z-20 flex h-full min-h-[140px] w-full max-w-[900px] items-center justify-around gap-6 px-3 @2xl:w-2/3 @2xl:justify-between @2xl:px-6 @4xl:w-4/5">
          {/*FIRST COLUMN*/}
          <FlexColumn className="flex justify-center py-2 text-center">
            <TitleLG className="hidden whitespace-nowrap pb-1 @3xl:block" weight="semibold" color="white">
              {t('buyEarlySaveBig', 'Buy Early. Save Big.')}
            </TitleLG>

            <HeadingSM
              weight="bold"
              className="break-none mb-1 whitespace-nowrap uppercase @5xl:!text-[2.5rem]"
              color="white"
            >
              {t('percentOff', '{{percent}}% off', {
                percent: Math.round(earlyBirdDiscount.activeChildDiscount.value * 100),
              })}
            </HeadingSM>

            <EyebrowLG
              weight={'medium'}
              className="flex flex-col items-center justify-center whitespace-nowrap uppercase @lg:mt-1 @lg:flex-row @lg:gap-2"
              color="white"
            >
              {t('useCode', 'Use Code')}
              <span className="px-2 py-[2px] font-bold text-white" style={{ backgroundColor: accentColor }}>
                {code}
              </span>
            </EyebrowLG>
            <FlexibleBookingToolTip
              className="mt-2 hidden @lg:block"
              buttonColor="white"
              buttonClassName="bg-none border-[1px] !border-core-gray-800 text-white focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent @3xl:hidden"
            />
          </FlexColumn>

          {/*SECOND COLUMN*/}
          <FlexColumn className="flex items-center justify-center text-center">
            {restrictionCopy && (
              <SpecificTimesToolTip
                textColor={'white'}
                buttonColor={'white'}
                buttonClassName="flex items-center opacity-70 focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent whitespace-nowrap"
                copy={restrictionCopy}
              >
                <TitleXXS className="text-center @2xl:hidden" weight="semibold" color="white">
                  {t('selectShowtimes', 'Select Showtimes')}
                </TitleXXS>
                <TitleSM className="hidden text-center @2xl:block" weight="semibold" color="white">
                  {t('onSelectShowtimes', 'On Select Showtimes')}
                </TitleSM>
              </SpecificTimesToolTip>
            )}

            <div className="mt-2 @4xl:hidden">
              <CountdownTimer textColor="white" onCountdownEnd={refetch} />
            </div>

            <CaptionSM className="hidden pt-1 italic @4xl:block" color="core-gray-400">
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>

          {/*THIRD COLUMN*/}
          <FlexColumn className="hidden items-center justify-center @4xl:block">
            <CountdownTimer textColor="white" onCountdownEnd={refetch} />
            <div className="mt-2">
              <FlexibleBookingToolTip
                buttonColor={'core-gray-400'}
                textColor={'core-gray-400'}
                buttonClassName="bg-none border-[1px] !border-core-gray-500 text-core-gray-400 focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent whitespace-nowrap"
              />
            </div>
          </FlexColumn>
        </div>
      </div>
    </div>
  )
}
